import { motion, useReducedMotion } from "framer-motion";
import { FiArrowUpRight } from "react-icons/fi";
import ActionButtons from "../ActionButtons";
import HomeFeatureCards from "../HomeFeatureCards";
import { useDownloadAppModal } from "../../context/DownloadAppModalContext";

const easeSoft = [0.25, 0.1, 0.25, 1] as const;

const fadeUpLight = {
    hidden: { opacity: 0, y: 18 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: easeSoft },
    },
};

const slideSoftLeft = {
    hidden: { opacity: 0, x: -28 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.55, ease: easeSoft },
    },
};

const slideSoftRight = {
    hidden: { opacity: 0, x: 28 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.55, ease: easeSoft },
    },
};

const staggerLight = {
    hidden: {},
    visible: {
        transition: { staggerChildren: 0.09, delayChildren: 0.04 },
    },
};

export default function GoLimitlessSection() {
    const reduceMotion = useReducedMotion();
    const { openDownloadModal } = useDownloadAppModal();

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-8 md:pt-10 lg:pt-12 bg-[#FFF]">
            {/* Heading */}
            <motion.div
                variants={staggerLight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                className="text-center mb-5 md:mb-6"
            >
                <motion.h2
                    variants={fadeUpLight}
                    className="text-xl md:text-3xl lg:text-5xl font-display-fran font-[600] text-[#191A15] mb-2 leading-tight"
                >
                    Go Limitless with AfriPay
                </motion.h2>
            </motion.div>

            {/* Global Access Banner */}
            <motion.div
                variants={staggerLight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="bg-[#EBF1FF] pt-6 md:pt-10 pb-4 md:pb-8 pl-5 md:pl-20 pr-5 md:pr-16 flex flex-col md:flex-row items-center justify-between gap-6 rounded-3xl"
                style={{
                    backgroundImage: "url('/images/globalAccessBG.avif')",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                }}
            >
                <motion.div variants={slideSoftLeft} className="w-full md:max-w-[500px] text-[#0A0A0A]">
                    <div className="flex flex-col-reverse md:flex-col">
                        <p className="text-sm md:text-base mb-4">
                            Leave financial borders behind. Send, receive, and spend money anywhere in the world with total control and transparency.
                        </p>
                        <h1 className="text-3xl md:text-5xl font-medium mb-6">One Account. Truly Global Access.</h1>
                    </div>
                    <ActionButtons size="small" />
                </motion.div>

                <motion.div variants={slideSoftRight} className="relative">
                    <motion.img
                        src="/images/globalAccess.avif"
                        alt="Global Access"
                        className="w-full md:w-[560px] md:h-[408px]"
                        animate={reduceMotion ? undefined : { y: [0, -6, 0] }}
                        transition={reduceMotion ? undefined : { duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <motion.img
                        src="/images/us_globalAccess.png"
                        alt="US Dollar"
                        className="absolute top-0 -right-3.5 md:-top-11 md:-right-16 w-[100px] md:w-[182px] md:h-12.5"
                        animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
                        transition={reduceMotion ? undefined : { duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                    />
                    <motion.img
                        src="/images/british_globalAccess.png"
                        alt="British Pound"
                        className="absolute -top-3.5 -left-3.5 w-[110px] md:-top-14 md:-left-14 md:w-[215px] md:h-12.5"
                        animate={reduceMotion ? undefined : { y: [0, -5, 0] }}
                        transition={reduceMotion ? undefined : { duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                    />
                    <motion.img
                        src="/images/euro_globalAccess.png"
                        alt="Euro"
                        className="absolute -bottom-10 left-[40%] w-[90px] md:left-1/2 md:w-[131px] md:h-12.5"
                        animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
                        transition={reduceMotion ? undefined : { duration: 5.2, repeat: Infinity, ease: "easeInOut", delay: 0.15 }}
                    />
                </motion.div>
            </motion.div>

            {/* Feature Cards Row */}
            <div className="bg-white pt-6 md:pt-8 pb-6 md:pb-10 xs:px-5 md:px-0 flex justify-center">
                <motion.div
                    variants={staggerLight}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    className="w-screen lg:w-auto flex flex-col lg:flex-row items-center gap-6 px-2 xs:px-5 lg:px-0"
                >
                    {/* Send Money Card */}
                    <motion.div
                        variants={slideSoftLeft}
                        whileHover={reduceMotion ? undefined : { scale: 1.01 }}
                        transition={{ type: "spring", stiffness: 400, damping: 28 }}
                        className="send-money-hero-bg w-full h-[400px] md:w-[579.5px] md:h-[624px] rounded-3xl flex flex-col justify-between pt-11 pb-6 px-2 md:px-8"
                    >
                        <div className="space-y-2 text-white">
                            <p className="text-xs">BUILT FOR WHAT'S NEXT</p>
                            <h2 className="text-xl xs:text-[32px] md:text-5xl">Send Money here to anywhere</h2>
                        </div>
                        <button
                            type="button"
                            onClick={openDownloadModal}
                            className="max-w-[242px] bg-[#9AE600] rounded-3xl flex items-center justify-center py-3 px-6 gap-2 cursor-pointer duration-300 transition-all hover:animate-bounce border-0"
                        >
                            <p className="text-black text-base text-nowrap">Create USD Account</p>
                            <FiArrowUpRight color="#000000" size={20} />
                        </button>
                    </motion.div>

                    {/* Right column: Hold Money + Track Finance */}
                    <motion.div variants={slideSoftRight} className="w-full lg:w-auto flex flex-col items-center md:items-start space-y-6">
                        <motion.div
                            className="overflow-hidden w-full lg:w-auto flex items-end justify-between md:gap-10 bg-[#E7F4C4] rounded-3xl pl-2 pr-0 md:pr-8 md:pl-8 pt-3"
                            variants={fadeUpLight}
                            whileHover={reduceMotion ? undefined : { y: -2 }}
                            transition={{ type: "spring", stiffness: 420, damping: 30 }}
                        >
                            <div className="min-w-0 flex-1 max-w-[272px] flex flex-col text-[#0A0A0A] pb-10">
                                <img src="/images/multiple_currency_content.png" alt="Multiple Currency Content" className="w-12 h-12 mb-[22px]" />
                                <p className="text-sm mb-2.5">Multiple currencies. One account. Zero hassle</p>
                                <h2 className="text-xl md:text-[30px] mb-4">Hold money in 30+ Currencies</h2>
                                <ActionButtons size="small" />
                            </div>
                            <img src="/images/multipleCurrencyPhone.avif" alt="Hold Money" className="max-w-[214px] h-[250px] hidden md:block shrink-0" />
                            <img src="/images/multipleCurrencyPhoneMobile.avif" alt="Hold Money" className="w-20 h-[250px] block md:hidden shrink-0" />
                        </motion.div>

                        <motion.div
                            className="overflow-hidden w-full lg:w-auto flex items-end justify-between md:gap-10 bg-[#FFF9C8] rounded-3xl pl-2 pr-0 md:pr-8 md:pl-8 pt-3"
                            variants={fadeUpLight}
                            whileHover={reduceMotion ? undefined : { y: -2 }}
                            transition={{ type: "spring", stiffness: 420, damping: 30 }}
                        >
                            <div className="min-w-0 flex-1 max-w-[272px] flex flex-col text-[#0A0A0A] pt-1.5 pb-10">
                                <FiArrowUpRight size={24} className="mb-[22px]" />
                                <p className="text-sm mb-2.5">Monitor where your maintenance budget goes & identify cost saving opportunities</p>
                                <h2 className="text-xl md:text-[30px] mb-4">Track your finance spending</h2>
                                <ActionButtons size="small" />
                            </div>
                            <img src="/images/TrackFinancePhone.avif" alt="Track Finance" className="max-w-[214px] h-[250px] hidden md:block shrink-0" />
                            <img src="/images/TrackFinancePhoneMobile.avif" alt="Track Finance" className="w-20 h-[250px] block md:hidden shrink-0" />
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>

            <HomeFeatureCards />

            {/* Shop & Spend / AfriPay Coin row */}
            {/* <ShopAndCoinSection reduceMotion={!!reduceMotion} openDownloadModal={openDownloadModal} /> */}
        </div>
    );
}

/* ─── Shop & Coin sub-section ─────────────────────────────────────── */

interface ShopAndCoinProps {
    reduceMotion: boolean;
    openDownloadModal: () => void;
}

const slideInLeft = {
    hidden: { opacity: 0, x: -80 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const slideInRight = {
    hidden: { opacity: 0, x: 80 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2 } },
};

function ShopAndCoinSection({ reduceMotion, openDownloadModal }: ShopAndCoinProps) {
    return (
        <section className="w-full mb-8 md:mb-10">
            <div className="max-w-7xl mx-auto px-4 md:px-5">
                <motion.div
                    variants={staggerLight}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.22 }}
                    className="grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden border border-[#D1DBE5] shadow-[0_2px_20px_-6px_rgba(15,35,52,0.12)]"
                >
                    {/* Shop And Spend */}
                    <motion.div
                        variants={slideSoftLeft}
                        whileHover={reduceMotion ? undefined : { scale: 1.008 }}
                        transition={{ type: "spring", stiffness: 420, damping: 35 }}
                        className="bg-[#0B7F47] text-white flex flex-col md:flex-row md:items-end md:min-h-[240px] lg:min-h-[260px] origin-center"
                    >
                        <div className="p-4 sm:p-5 md:p-6 flex flex-col justify-center flex-1 min-w-0 md:max-w-[52%]">
                            <h2 className="font-display-fran font-semibold text-[20px] sm:text-[22px] md:text-2xl leading-[1.15] tracking-[-0.02em] mb-2">
                                Shop And Spend Globally
                            </h2>
                            <p className="text-[12px] md:text-[13px] text-white/90 leading-snug mb-3 md:mb-4 max-w-[20rem]">
                                Make international payments with a virtual card that just works.
                            </p>
                            <button
                                type="button"
                                onClick={openDownloadModal}
                                className="self-start flex items-center gap-2 text-white text-xs md:text-sm font-semibold hover:gap-2.5 transition-all group"
                            >
                                <span className="border-b border-white/30 group-hover:border-white transition-colors">Get your card</span>
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/40 bg-white/10">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m0 0l-3-3m3 3l-3 3" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                        <div className="flex-1 flex items-end justify-center md:justify-end px-3 pb-1 pt-2 md:px-4 md:pt-0 md:pb-1 md:pr-1">
                            <motion.img
                                src="/images/shopAndSpend2.avif"
                                alt="AfriPay virtual cards — shop globally"
                                className="hidden md:block w-full max-h-[200px] lg:max-h-[228px] h-auto object-contain object-bottom"
                                animate={reduceMotion ? undefined : { y: [0, -5, 0] }}
                                transition={reduceMotion ? undefined : { duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                            />
                            <motion.img
                                src="/images/shopAndSpendMobile.avif"
                                alt="AfriPay virtual cards — shop globally"
                                className="md:hidden w-full max-w-[260px] h-auto object-contain mx-auto"
                                animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
                                transition={reduceMotion ? undefined : { duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            />
                        </div>
                    </motion.div>

                    {/* AfriPay Coin */}
                    <motion.div
                        variants={slideSoftRight}
                        whileHover={reduceMotion ? undefined : { scale: 1.008 }}
                        transition={{ type: "spring", stiffness: 420, damping: 35 }}
                        className="bg-gradient-to-br from-[#e8edf4] via-[#f0f4f8] to-[#e4f0ea] flex flex-col-reverse md:flex-row md:items-center gap-3 md:gap-4 p-4 sm:p-5 md:p-6 md:min-h-[240px] lg:min-h-[260px] border-t md:border-t-0 md:border-l border-[#D1DBE5]/90 origin-center"
                    >
                        <div className="shrink-0 flex justify-center md:justify-start md:w-[42%]">
                            <motion.img
                                src="/images/rewards.avif"
                                alt="AfriPay Coin — referrals and rewards"
                                className="hidden md:block w-full h-auto max-h-[200px] lg:max-h-[220px] object-contain object-left"
                                animate={reduceMotion ? undefined : { y: [0, -5, 0] }}
                                transition={reduceMotion ? undefined : { duration: 6.2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                            />
                            <motion.img
                                src="/images/rewardsMobile.avif"
                                alt="AfriPay Coin — referrals and rewards"
                                className="md:hidden w-full max-w-[160px] h-auto object-contain mx-auto"
                                animate={reduceMotion ? undefined : { y: [0, -4, 0] }}
                                transition={reduceMotion ? undefined : { duration: 5.4, repeat: Infinity, ease: "easeInOut", delay: 0.25 }}
                            />
                        </div>
                        <div className="flex flex-col justify-center flex-1 min-w-0">
                            <p className="text-[#0B7F47] text-[10px] font-semibold uppercase tracking-[0.14em] mb-1">Rewards</p>
                            <h2 className="font-display-fran font-semibold text-[#030319] text-lg md:text-xl leading-[1.15] tracking-[-0.02em] mb-1.5">
                                AfriPay Coin
                            </h2>
                            <p className="text-[#475467] text-[12px] md:text-[13px] leading-snug">
                                Refer friends and family, earn AfriPay Coins, and use them for rewards inside the app.
                            </p>
                            <button
                                type="button"
                                onClick={openDownloadModal}
                                className="mt-2.5 md:mt-3 group inline-flex items-center gap-1.5 text-left text-[#0B7F47] text-xs md:text-[13px] font-semibold hover:text-[#096339] transition-colors"
                            >
                                <span className="border-b border-transparent group-hover:border-[#096339] transition-colors">Refer now</span>
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-[#0B7F47]/30 bg-white/55 text-[#0B7F47] shadow-sm group-hover:bg-white transition-colors">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}